import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

export interface FrontendUser {
  id: string;
  email: string;
  name: string;
  role: string;
  org_id?: string;
}

export interface CognitoAuthResult {
  idToken: string;
  user: FrontendUser;
}

let userPool: CognitoUserPool | null = null;

const requiredEnv = (name: string, value?: string): string => {
  if (!value) throw new Error(`Missing frontend configuration: ${name}`);
  return value;
};

const pool = (): CognitoUserPool => {
  if (!userPool) {
    userPool = new CognitoUserPool({
      UserPoolId: requiredEnv(
        'REACT_APP_COGNITO_USER_POOL_ID',
        process.env.REACT_APP_COGNITO_USER_POOL_ID,
      ),
      ClientId: requiredEnv(
        'REACT_APP_COGNITO_APP_CLIENT_ID',
        process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
      ),
      // Keep Cognito's refresh/access session out of localStorage. The application
      // stores only the IdToken under the legacy-compatible "token" key.
      Storage: window.sessionStorage,
    });
  }
  return userPool;
};

export const mapIdTokenClaims = (claims: Record<string, unknown>): FrontendUser => {
  const id = String(claims.sub || '');
  const email = String(claims.email || claims['cognito:username'] || '');
  if (!id || !email) throw new Error('Cognito IdToken is missing required user claims');

  return {
    id,
    email,
    name: String(claims.name || email),
    org_id: claims['custom:org_id'] ? String(claims['custom:org_id']) : undefined,
    role: String(claims['custom:role'] || 'farmer'),
  };
};

export const persistIdToken = (idToken: string): void => {
  localStorage.setItem('token', idToken);
};

const resultFromSession = (session: CognitoUserSession): CognitoAuthResult => {
  const token = session.getIdToken();
  const idToken = token.getJwtToken();
  persistIdToken(idToken);
  return { idToken, user: mapIdTokenClaims(token.payload) };
};

const friendlyCognitoError = (error: unknown): Error => {
  const candidate = (error || {}) as { code?: string; message?: string };
  if (candidate.code === 'NotAuthorizedException' || candidate.code === 'UserNotFoundException') {
    return new Error('Adresse e-mail ou mot de passe incorrect.');
  }
  if (candidate.code === 'UserNotConfirmedException') {
    return new Error('Ce compte Cognito doit être confirmé avant la connexion.');
  }
  if (candidate.code === 'PasswordResetRequiredException') {
    return new Error('Une réinitialisation du mot de passe Cognito est requise.');
  }
  return new Error(candidate.message || 'Échec de la connexion Cognito.');
};

export const authenticateWithCognito = (
  email: string,
  password: string,
): Promise<CognitoAuthResult> =>
  new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: email, Pool: pool() });
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => resolve(resultFromSession(session)),
      onFailure: (error) => reject(friendlyCognitoError(error)),
      newPasswordRequired: () =>
        reject(new Error('Un nouveau mot de passe Cognito doit être défini par un administrateur.')),
      mfaRequired: () =>
        reject(new Error('Le défi MFA Cognito n’est pas encore pris en charge par cette interface.')),
      totpRequired: () =>
        reject(new Error('Le défi TOTP Cognito n’est pas encore pris en charge par cette interface.')),
    });
  });

export const restoreCognitoSession = (): Promise<CognitoAuthResult | null> =>
  new Promise((resolve, reject) => {
    const cognitoUser = pool().getCurrentUser();
    if (!cognitoUser) {
      localStorage.removeItem('token');
      resolve(null);
      return;
    }
    cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) {
        localStorage.removeItem('token');
        reject(friendlyCognitoError(error));
        return;
      }
      resolve(resultFromSession(session));
    });
  });

export const changeCognitoPassword = (
  currentPassword: string,
  newPassword: string,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const cognitoUser = pool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('Aucune session Cognito active.'));
      return;
    }
    cognitoUser.getSession((sessionError: Error | null, session: CognitoUserSession | null) => {
      if (sessionError || !session?.isValid()) {
        reject(friendlyCognitoError(sessionError));
        return;
      }
      cognitoUser.changePassword(currentPassword, newPassword, (error) => {
        if (error) reject(friendlyCognitoError(error));
        else resolve();
      });
    });
  });

export const signOutCognito = (): void => {
  pool().getCurrentUser()?.signOut();
  localStorage.removeItem('token');
};
